// ./src/components/features/Travels/TravelFilters/TravelFilters.tsx
//
// Travel-specific filters wrapper around ExpenseFiltersToolbar.
// Adds travel type and status filters as extraFilters (in drawer)
// and extraChips (in the active filters row).
//
// Draft state pattern:
// - Maintains draft values for travelTypes and travelStatuses internally.
// - Exposes an ExtraFiltersHandle via ref so ExpenseFiltersDrawer can
//   call onOpen (sync applied → draft), onApply (commit draft → applied),
//   onClear (reset drafts), and hasDraftValues (for Clear button visibility).
// - The extraFilters rendered in the drawer bind to draft values, so
//   selecting a travel type or status does NOT trigger a data reload until
//   the user clicks Apply.
// - extraChips bind to the APPLIED values (from props), so chips only
//   appear after Apply is clicked.

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Text, Badge, CloseButton, MultiSelect, MultiSelectProps, Group, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  TRAVEL_TYPE_OPTIONS,
  TravelTypeValue,
  TRAVEL_STATUS_OPTIONS,
  TravelStatusValue,
} from "@/config";

import { ExpenseFiltersToolbar } from "@/components/features/ExpenseFiltersToolbar";
import { ExtraFiltersHandle } from "@/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer";

import { TravelTypeMultiSelector } from "../TravelTypeMultiSelector/TravelTypeMultiSelector";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TravelFiltersProps {
  /** Handler for add button click (Start Travel) */
  onAdd?: () => void;

  /** Handler for add past travel button click */
  onAddPast?: () => void;

  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether add button is disabled */
  addDisabled?: boolean;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Selected applied travel types */
  travelTypes?: TravelTypeValue[];

  /** Callback when travel types are applied */
  onTravelTypesChange?: (types: TravelTypeValue[]) => void;

  /** Selected applied travel statuses */
  travelStatuses?: TravelStatusValue[];

  /** Callback when travel statuses are applied */
  onTravelStatusesChange?: (statuses: TravelStatusValue[]) => void;

  /** Show car filter (default: true) */
  showCarFilter?: boolean;

  /** Show date range filter (default: true) */
  showDateFilter?: boolean;

  /** Show tags filter (default: true) */
  showTagFilter?: boolean;

  /** Show travel type filter (default: true) */
  showTravelTypeFilter?: boolean;

  /** Show travel status filter (default: true) */
  showTravelStatusFilter?: boolean;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Passed through to ExpenseFiltersToolbar.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;
}

// ============================================
// Option Data Interfaces
// ============================================

interface TravelStatusOptionData {
  value: string;
  label: string;
  iconName: string;
  color: string;
}

// ============================================
// Render Option Components
// ============================================

const renderTravelStatusOption: MultiSelectProps["renderOption"] = ({
  option,
}: any) => {
  const optionData = option as TravelStatusOptionData;
  return (
    <Group gap="sm" wrap="nowrap">
      <AppIcon
        iconName={optionData.iconName}
        size={18}
        color={`var(--mantine-color-${optionData.color}-6)`}
      />
      <Text size="sm">{optionData.label}</Text>
    </Group>
  );
};

// ============================================
// Component
// ============================================

const TravelFiltersComponent: React.FC<TravelFiltersProps> = ({
  onAdd,
  onAddPast,
  onRefresh,
  addDisabled = false,
  refreshDisabled = false,
  loading = false,
  travelTypes = [],
  onTravelTypesChange,
  travelStatuses = [],
  onTravelStatusesChange,
  showDateFilter = true,
  showTagFilter = true,
  showTravelTypeFilter = true,
  showTravelStatusFilter = false,
  enableShortcuts = true,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["travels", "common"]);

  // ============================================
  // Draft State (used inside the drawer only)
  // ============================================

  const [draftTravelTypes, setDraftTravelTypes] = useState<TravelTypeValue[]>([]);
  const [draftTravelStatuses, setDraftTravelStatuses] = useState<TravelStatusValue[]>([]);

  // Refs to track latest applied values for getValues
  const travelTypesRef = useRef(travelTypes);
  travelTypesRef.current = travelTypes;
  const travelStatusesRef = useRef(travelStatuses);
  travelStatusesRef.current = travelStatuses;

  // ============================================
  // ExtraFiltersHandle — lifecycle contract with ExpenseFiltersDrawer
  // ============================================

  const extraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      setDraftTravelTypes(travelTypes);
      setDraftTravelStatuses(travelStatuses);
    },
    onApply: () => {
      onTravelTypesChange?.(draftTravelTypes);
      onTravelStatusesChange?.(draftTravelStatuses);
      // Return the values being applied so the toolbar can include them
      // in onFiltersChange synchronously (context hasn't flushed yet)
      const values: Record<string, unknown> = {};
      if (draftTravelTypes.length > 0) {
        values.travelType = draftTravelTypes;
      }
      if (draftTravelStatuses.length > 0) {
        values.status = draftTravelStatuses;
      }
      return values;
    },
    onClear: () => {
      setDraftTravelTypes([]);
      setDraftTravelStatuses([]);
    },
    hasDraftValues: () => {
      return draftTravelTypes.length > 0 || draftTravelStatuses.length > 0;
    },
    getValues: () => {
      // Return current applied (not draft) values for non-Apply emit paths
      // (e.g. chip removal, search keyword change, initial mount)
      const values: Record<string, unknown> = {};
      if (travelTypesRef.current.length > 0) {
        values.travelType = travelTypesRef.current;
      }
      if (travelStatusesRef.current.length > 0) {
        values.status = travelStatusesRef.current;
      }
      return values;
    },
  });

  // Keep the ref callbacks up to date with latest state/props
  extraFiltersRef.current.onOpen = () => {
    setDraftTravelTypes(travelTypes);
    setDraftTravelStatuses(travelStatuses);
  };
  extraFiltersRef.current.onApply = () => {
    onTravelTypesChange?.(draftTravelTypes);
    onTravelStatusesChange?.(draftTravelStatuses);
    const values: Record<string, unknown> = {};
    if (draftTravelTypes.length > 0) {
      values.travelType = draftTravelTypes;
    }
    if (draftTravelStatuses.length > 0) {
      values.status = draftTravelStatuses;
    }
    return values;
  };
  extraFiltersRef.current.onClear = () => {
    setDraftTravelTypes([]);
    setDraftTravelStatuses([]);
  };
  extraFiltersRef.current.hasDraftValues = () => {
    return draftTravelTypes.length > 0 || draftTravelStatuses.length > 0;
  };
  extraFiltersRef.current.getValues = () => {
    const values: Record<string, unknown> = {};
    if (travelTypesRef.current.length > 0) {
      values.travelType = travelTypesRef.current;
    }
    if (travelStatusesRef.current.length > 0) {
      values.status = travelStatusesRef.current;
    }
    return values;
  };

  // ============================================
  // Travel Status Options
  // ============================================

  const travelStatusOptions = useMemo((): TravelStatusOptionData[] => {
    return TRAVEL_STATUS_OPTIONS.map((config) => ({
      value: String(config.value),
      label: t(config.translationKey, { defaultValue: String(config.value) }),
      iconName: config.icon,
      color: config.color,
    }));
  }, [t]);

  // ============================================
  // Handlers — Draft changes (inside drawer)
  // ============================================

  const handleDraftTravelTypesChange = useCallback(
    (values: TravelTypeValue[]) => {
      setDraftTravelTypes(values);
    },
    []
  );

  const handleDraftTravelStatusesChange = useCallback(
    (values: string[]) => {
      setDraftTravelStatuses(values.map((v) => Number(v)) as TravelStatusValue[]);
    },
    []
  );

  // ============================================
  // Handlers — Chip removal (removes applied values directly)
  // ============================================

  const handleRemoveTravelType = useCallback(
    (typeToRemove: TravelTypeValue) => {
      onTravelTypesChange?.(travelTypes.filter((t) => t !== typeToRemove));
    },
    [travelTypes, onTravelTypesChange]
  );

  const handleRemoveTravelStatus = useCallback(
    (statusToRemove: TravelStatusValue) => {
      onTravelStatusesChange?.(travelStatuses.filter((s) => s !== statusToRemove));
    },
    [travelStatuses, onTravelStatusesChange]
  );

  // ============================================
  // Build Extra Chips (based on APPLIED values, not drafts)
  // ============================================

  const extraChips = useMemo(() => {
    const chips: React.ReactNode[] = [];

    // Travel type chips
    if (showTravelTypeFilter && travelTypes.length > 0) {
      travelTypes.forEach((typeValue) => {
        const config = TRAVEL_TYPE_OPTIONS.find((opt) => opt.value === typeValue);
        if (!config) return;

        const label = t(config.translationKey, { defaultValue: config.value });

        chips.push(
          <Badge
            key={`travel-type-${typeValue}`}
            variant="light"
            color={config.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={() => handleRemoveTravelType(typeValue)}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {label}
          </Badge>
        );
      });
    }

    // Travel status chips
    if (showTravelStatusFilter && travelStatuses.length > 0) {
      travelStatuses.forEach((statusValue) => {
        const config = TRAVEL_STATUS_OPTIONS.find((opt) => opt.value === statusValue);
        if (!config) return;

        const label = t(config.translationKey, { defaultValue: String(config.value) });

        chips.push(
          <Badge
            key={`travel-status-${statusValue}`}
            variant="light"
            color={config.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={() => handleRemoveTravelStatus(statusValue)}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {label}
          </Badge>
        );
      });
    }

    if (chips.length === 0) return undefined;
    return <>{chips}</>;
  }, [
    showTravelTypeFilter,
    showTravelStatusFilter,
    travelTypes,
    travelStatuses,
    handleRemoveTravelType,
    handleRemoveTravelStatus,
    t,
  ]);

  // ============================================
  // Build Extra Filters Content (for drawer — uses DRAFT values)
  // ============================================

  const extraFilters = useMemo(() => {
    const items: React.ReactNode[] = [];

    // Travel Type Filter — chip-based multi-selector
    if (showTravelTypeFilter) {
      items.push(
        <Box key="travel-type-filter">
          <TravelTypeMultiSelector
            value={draftTravelTypes}
            onChange={handleDraftTravelTypesChange}
            label={t("travels:filters.travelType.label", {
              defaultValue: "Travel Type",
            })}
          />
        </Box>
      );
    }

    // Travel Status Filter — MultiSelect dropdown
    if (showTravelStatusFilter) {
      items.push(
        <Box key="travel-status-filter">
          <Text size="sm" fw={500} mb={6} c="dimmed">
            {t("travels:filters.status.label", { defaultValue: "Status" })}
          </Text>
          <MultiSelect
            data={travelStatusOptions}
            value={draftTravelStatuses.map(String)}
            onChange={handleDraftTravelStatusesChange}
            placeholder={t("travels:filters.status.placeholder")}
            clearable
            searchable={false}
            maxDropdownHeight={280}
            renderOption={renderTravelStatusOption}
            size="md"
            comboboxProps={{ withinPortal: false }}
          />
        </Box>
      );
    }

    if (items.length === 0) return null;

    return (
      <Stack gap="md">
        {items}
      </Stack>
    );
  }, [
    showTravelTypeFilter,
    showTravelStatusFilter,
    travelStatusOptions,
    draftTravelTypes,
    draftTravelStatuses,
    handleDraftTravelTypesChange,
    handleDraftTravelStatusesChange,
    t,
  ]);

  // ============================================
  // Build Extra Actions (Add Past Travel button)
  // ============================================

  const extraActions = useMemo(() => {
    if (!onAddPast) return null;

    return (
      <Box key="add-past-action" visibleFrom="sm">
        {/* Desktop only - on mobile it's in the drawer */}
      </Box>
    );
  }, [onAddPast]);

  // ============================================
  // Render
  // ============================================

  return (
    <ExpenseFiltersToolbar
      entityType="travels"
      onAdd={onAdd}
      onRefresh={onRefresh}
      addDisabled={addDisabled}
      refreshDisabled={refreshDisabled}
      loading={loading}
      addLabel={t("common:buttons.add")}
      carFilterMode="single"
      showDateFilter={showDateFilter}
      showTagFilter={showTagFilter}
      enableShortcuts={enableShortcuts}
      extraFilters={extraFilters}
      extraActions={extraActions}
      extraChips={extraChips}
      extraFiltersRef={extraFiltersRef}
      onFiltersChange={onFiltersChange}
    />
  );
};

export const TravelFilters = React.memo(TravelFiltersComponent);